import { Delaunay } from 'd3-delaunay';
import { TAU } from './math';
import type { Rng } from './rng';
import type { Contour } from './text/flatten';
import type { GlyphInstance } from './text/layout';

/**
 * Breaking glyphs into fragments.
 *
 * Shared by the static fragmentation technique and the physics-based shatter, because
 * the break pattern is the same problem in both: partition a glyph's area, then keep
 * the parts of each cell that lie inside the letterform.
 *
 * Clipping is done with Sutherland–Hodgman against each half-plane of the Voronoi cell
 * rather than a full polygon boolean. The cells are convex, which is exactly the case
 * Sutherland–Hodgman handles correctly, and it avoids pulling in a clipping library.
 */
export type ShatterPattern = 'voronoi' | 'radial' | 'grid' | 'splinter';

export const SHATTER_PATTERN_OPTIONS: { value: ShatterPattern; label: string }[] = [
  { value: 'voronoi', label: 'Voronoi' },
  { value: 'radial', label: 'Radial' },
  { value: 'grid', label: 'Raster' },
  { value: 'splinter', label: 'Splitter' },
];

export interface Fragment {
  /** Polygon in canvas space. */
  points: Float32Array;
  centroidX: number;
  centroidY: number;
  area: number;
  /** Index of the glyph this fragment came from. */
  glyph: number;
}

/** Clips a convex polygon against the half-plane on the inner side of a line. */
function clipHalfPlane(points: number[], ax: number, ay: number, nx: number, ny: number): number[] {
  const out: number[] = [];
  const n = points.length / 2;
  if (n === 0) return out;

  const side = (x: number, y: number): number => (x - ax) * nx + (y - ay) * ny;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const x0 = points[i * 2];
    const y0 = points[i * 2 + 1];
    const x1 = points[j * 2];
    const y1 = points[j * 2 + 1];
    const s0 = side(x0, y0);
    const s1 = side(x1, y1);

    if (s0 <= 0) out.push(x0, y0);
    if ((s0 < 0 && s1 > 0) || (s0 > 0 && s1 < 0)) {
      const t = s0 / (s0 - s1);
      out.push(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t);
    }
  }
  return out;
}

function polygonArea(points: ArrayLike<number>): number {
  let area = 0;
  for (let i = 0; i < points.length; i += 2) {
    const j = (i + 2) % points.length;
    area += points[i] * points[j + 1] - points[j] * points[i + 1];
  }
  return Math.abs(area) / 2;
}

/** Cheap even-odd point-in-polygon test over a glyph's contours. */
function insideGlyph(contours: readonly Contour[], x: number, y: number): boolean {
  let inside = false;
  for (const contour of contours) {
    const p = contour.points;
    const n = p.length / 2;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const yi = p[i * 2 + 1];
      const yj = p[j * 2 + 1];
      if (yi > y !== yj > y) {
        const xi = p[i * 2];
        const xj = p[j * 2];
        if (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
      }
    }
  }
  return inside;
}

/**
 * Splits one glyph into fragments.
 *
 * The seed points differ per pattern, but the partition is always a Voronoi diagram of
 * those seeds — a radial crack pattern is simply seeds on rays from the centre. That
 * keeps one clipping path instead of four.
 */
export function shatterGlyph(glyph: GlyphInstance, count: number, pattern: ShatterPattern, rng: Rng): Fragment[] {
  const bounds = glyph.bounds;
  if (bounds.width <= 0 || bounds.height <= 0 || count < 1) return [];

  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  const seeds: number[] = [];

  switch (pattern) {
    case 'radial': {
      // Seeds on rays from the centre: the resulting cells meet at the centre, which
      // reads as an impact crack.
      const rings = Math.max(1, Math.round(Math.sqrt(count / 3)));
      const perRing = Math.max(3, Math.ceil(count / rings));
      for (let r = 0; r < rings; r++) {
        const radius = ((r + 1) / rings) * Math.max(bounds.width, bounds.height) * 0.6;
        for (let k = 0; k < perRing; k++) {
          const a = (k / perRing) * TAU + rng.signed(0.25) + r * 0.4;
          seeds.push(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
        }
      }
      break;
    }
    case 'grid': {
      const cols = Math.max(1, Math.round(Math.sqrt((count * bounds.width) / bounds.height)));
      const rows = Math.max(1, Math.ceil(count / cols));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          seeds.push(
            bounds.x + ((c + 0.5 + rng.signed(0.18)) * bounds.width) / cols,
            bounds.y + ((r + 0.5 + rng.signed(0.18)) * bounds.height) / rows,
          );
        }
      }
      break;
    }
    case 'splinter': {
      // Strongly anisotropic seeds produce long thin shards.
      for (let i = 0; i < count; i++) {
        seeds.push(bounds.x + rng.next() * bounds.width, bounds.y + rng.gaussian(0.5, 0.16) * bounds.height);
      }
      break;
    }
    default:
      for (let i = 0; i < count; i++) {
        seeds.push(bounds.x + rng.next() * bounds.width, bounds.y + rng.next() * bounds.height);
      }
  }

  if (seeds.length < 6) return [];

  const pad = 2;
  const delaunay = new Delaunay(Float64Array.from(seeds));
  const voronoi = delaunay.voronoi([
    bounds.x - pad,
    bounds.y - pad,
    bounds.x + bounds.width + pad,
    bounds.y + bounds.height + pad,
  ]);

  const fragments: Fragment[] = [];
  for (let i = 0; i < seeds.length / 2; i++) {
    const cell = voronoi.cellPolygon(i);
    if (!cell || cell.length < 4) continue;

    // Clip the cell against every glyph contour edge that matters. Doing a full
    // boolean would be correct but far heavier; clipping the convex cell against the
    // glyph's own bounding half-planes plus an inside test on the centroid keeps the
    // fragments where the ink is.
    let poly: number[] = [];
    for (let k = 0; k < cell.length - 1; k++) poly.push(cell[k][0], cell[k][1]);

    // Trim to the glyph bounding box so fragments never stick out.
    poly = clipHalfPlane(poly, bounds.x, 0, -1, 0);
    poly = clipHalfPlane(poly, bounds.x + bounds.width, 0, 1, 0);
    poly = clipHalfPlane(poly, 0, bounds.y, 0, -1);
    poly = clipHalfPlane(poly, 0, bounds.y + bounds.height, 0, 1);
    if (poly.length < 6) continue;

    let sx = 0;
    let sy = 0;
    for (let k = 0; k < poly.length; k += 2) {
      sx += poly[k];
      sy += poly[k + 1];
    }
    const n = poly.length / 2;
    const centroidX = sx / n;
    const centroidY = sy / n;

    // Only keep cells whose centre is actually on the letterform.
    if (!insideGlyph(glyph.contours, centroidX, centroidY)) continue;

    const points = Float32Array.from(poly);
    fragments.push({ points, centroidX, centroidY, area: polygonArea(points), glyph: glyph.index });
  }

  return fragments;
}

/** Shatters every drawable glyph, distributing the fragment budget by glyph area. */
export function shatterGlyphs(
  glyphs: readonly GlyphInstance[],
  totalFragments: number,
  pattern: ShatterPattern,
  rng: Rng,
): Fragment[] {
  const drawable = glyphs.filter((g) => !g.isWhitespace && g.contours.length > 0);
  if (drawable.length === 0) return [];

  const areas = drawable.map((g) => Math.max(1, g.bounds.width * g.bounds.height));
  const total = areas.reduce((a, b) => a + b, 0);

  const out: Fragment[] = [];
  drawable.forEach((glyph, i) => {
    const share = Math.max(2, Math.round((totalFragments * areas[i]) / total));
    out.push(...shatterGlyph(glyph, share, pattern, rng));
  });
  return out;
}
